import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__slider } from '@/components/ui/slider/UI_PRIMITIVE__slider';
import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { service_open_pose } from '@/services/open_pose/service_open_pose';
import { LOCAL__ui_set_number } from '@/state/ui/_base/LOCAL__ui_set_number';
import { assert_array_has_1_item } from '@shared/asserts/utils_asserts';
import { useRef, useState } from 'react';
import { SidebarNavigationDesktop } from '../_base/sidebar_navigation/desktop/SidebarNavigationDesktop';
import { ConstantsOpenPose } from './constants_open_pose';
import { EOpenPoseInteractionMode } from './enums/e_open_pose_interaction_mode';
import { use_open_pose_editor } from './hooks/use_open_pose_editor';

export function PageOpenPoseView() {
  /*
   * FIELDS
   */
  const dispatch = use_dispatch();
  const { is_service_open_pose_init } = use_slice_ui(['is_service_open_pose_init']);

  const ref_canvas_open_pose_scene = useRef<HTMLCanvasElement>(ENUM__html_ref_stub.CANVAS);

  const open_pose_editor = use_open_pose_editor();

  const [is_camera_perspective, set_is_camera_perspective] = useState(true);

  const [slider_value, set_slider_value] = useState<number>(ConstantsOpenPose.DEFAULT_BODY_SIZE);
  const [interaction_mode_current, set_interaction_mode_current] = useState<EOpenPoseInteractionMode>(EOpenPoseInteractionMode.DRAG_NODE_FIXED_LENGTH);

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (ref_canvas_open_pose_scene.current === ENUM__html_ref_stub.CANVAS) return;
    if (is_service_open_pose_init) return;

    service_open_pose.init(ref_canvas_open_pose_scene.current);
  }, [ref_canvas_open_pose_scene.current]);

  use_effect(() => {
    return () => service_open_pose.dispose();
  });

  /*
   * EVENTS
   */

  function on_toggle_camera() {
    open_pose_editor.toggle_camera();
    const is_camera_now = service_open_pose.camera_controller.is_camera_perspective;
    set_is_camera_perspective(is_camera_now);
  }

  function on_reset_scene() {
    on_slider_change([ConstantsOpenPose.DEFAULT_BODY_SIZE]);
    open_pose_editor.reset_body();
  }

  function on_reset_camera() {
    open_pose_editor.reset_camera();
    const is_camera_now = service_open_pose.camera_controller.is_camera_perspective;
    set_is_camera_perspective(is_camera_now);
  }

  function on_slider_change(value: number[]) {
    if (value[0] === slider_value) return;
    set_slider_value(value[0]);
    dispatch(LOCAL__ui_set_number({ key: 'open_pose_body_size', value: value[0] }));
  }

  function on_slider_commit(value_range: number[]) {
    let value = assert_array_has_1_item(value_range);
    dispatch(LOCAL__ui_set_number({ key: 'open_pose_body_size', value }));
  }

  function on_set_interaction_mode(mode: EOpenPoseInteractionMode) {
    console.log('🔴🔴🔴 ROTATE BUTTON CLICKED! MODE:', mode, '🔴🔴🔴');
    console.log('[UI] on_set_interaction_mode called with mode:', mode);
    console.log('[UI] open_pose_editor:', open_pose_editor);
    set_interaction_mode_current(mode);
    if (open_pose_editor && open_pose_editor.set_interaction_mode) {
      console.log('[UI] Calling open_pose_editor.set_interaction_mode');
      open_pose_editor.set_interaction_mode(mode);
    } else {
      console.error('[UI] open_pose_editor or set_interaction_mode not available');
    }
    console.log('[UI] on_set_interaction_mode completed');
  }

  /*
   * METHODS
   */

  function get_mode_button_style(mode: EOpenPoseInteractionMode) {
    return interaction_mode_current === mode ? 'w-full bg-blue-600 text-white font-semibold' : 'w-full bg-component text-page-foreground';
  }

  function get_mode_display_name(mode: EOpenPoseInteractionMode): string {
    let out_map = new Map([
      [EOpenPoseInteractionMode.ROTATE, 'Rotate Mode'],
      [EOpenPoseInteractionMode.DRAG_NODE, 'Drag Node (Variable Length)'],
      [EOpenPoseInteractionMode.DRAG_NODE_FIXED_LENGTH, 'Drag Node (Fixed Length)'],
    ]);

    let out = out_map.get_or_assert(mode);

    return out;
  }

  /*
   * HTML
   */
  return (
    <div className="relative flex w-full h-screen text-center">
      <SidebarNavigationDesktop />

      <div className="bg-[rgb(214,214,214)] relative" style={{ width: `calc(100vw - ${CONSTANTS__client.DESKTOP_NAV_BAR_WIDTH}px)`, height: '100vh' }}>
        <div className="absolute top-4 right-4 z-10 w-[320px] flex flex-col gap-2 bg-bar p-3 pb-4 rounded-md shadow-lg">
          <div className="flex flex-col items-center justify-start gap-2 pb-2 border-b border-gray-300">
            <UI_PRIMITIVE__button className="w-full bg-component text-page-foreground" onClick={on_toggle_camera} disabled={!open_pose_editor}>
              {is_camera_perspective ? 'Switch to Orthographic' : 'Switch to Perspective'}
            </UI_PRIMITIVE__button>

            <div className="w-full flex gap-2 items-center justify-center">
              <UI_PRIMITIVE__button className="w-full bg-component text-page-foreground" onClick={on_reset_scene} disabled={!open_pose_editor}>
                Reset Body
              </UI_PRIMITIVE__button>
              <UI_PRIMITIVE__button className="w-full bg-component text-page-foreground" onClick={on_reset_camera} disabled={!open_pose_editor}>
                Reset Camera
              </UI_PRIMITIVE__button>
            </div>
          </div>

          <div className="flex flex-col gap-2 pb-2 border-b border-gray-300">
            <h3 className="text-sm font-medium text-page-foreground text-center">Interaction Mode</h3>

            <UI_PRIMITIVE__button
              className={get_mode_button_style(EOpenPoseInteractionMode.ROTATE)}
              onClick={() => on_set_interaction_mode(EOpenPoseInteractionMode.ROTATE)}
              disabled={!open_pose_editor}
            >
              {get_mode_display_name(EOpenPoseInteractionMode.ROTATE)}
            </UI_PRIMITIVE__button>

            <UI_PRIMITIVE__button
              className={get_mode_button_style(EOpenPoseInteractionMode.DRAG_NODE)}
              onClick={() => on_set_interaction_mode(EOpenPoseInteractionMode.DRAG_NODE)}
              disabled={!open_pose_editor}
            >
              {get_mode_display_name(EOpenPoseInteractionMode.DRAG_NODE)}
            </UI_PRIMITIVE__button>

            <UI_PRIMITIVE__button
              className={get_mode_button_style(EOpenPoseInteractionMode.DRAG_NODE_FIXED_LENGTH)}
              onClick={() => on_set_interaction_mode(EOpenPoseInteractionMode.DRAG_NODE_FIXED_LENGTH)}
              disabled={!open_pose_editor}
            >
              {get_mode_display_name(EOpenPoseInteractionMode.DRAG_NODE_FIXED_LENGTH)}
            </UI_PRIMITIVE__button>
          </div>

          <div className="w-full flex flex-col gap-2 justify-center items-start">
            <span className="text-page-foreground font-medium">Body Size: {slider_value}%</span>
            <UI_PRIMITIVE__slider
              min={1}
              max={100}
              value={[slider_value]}
              defaultValue={[slider_value]}
              onValueChange={on_slider_change}
              onValueCommit={on_slider_commit}
              className="w-full"
            />
          </div>
        </div>

        <canvas ref={ref_canvas_open_pose_scene} style={{ width: '100%', height: '100%', display: 'block' }} />
      </div>
    </div>
  );
}
